// Phase 3 — Global team switcher control. Lives in every Shell's topbar.
// Tells the user which team's data they're currently looking at, and lets
// them pick another. Elevated users get an "All Teams" option that drops
// every filter.

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Users, Check } from "lucide-react";
import { useActiveTeam } from "./ActiveTeam";

export default function TeamSwitcher() {
  const { activeTeam, availableTeams, setActiveTeam, isAllTeams, elevated } = useActiveTeam();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = isAllTeams ? "All Teams" : (activeTeam?.name || "Pick a team");
  const sublabel = isAllTeams ? "global view" : (activeTeam?.sport || "");

  function pick(id) {
    setActiveTeam(id);
    setOpen(false);
  }

  return (
    <div className="teamSwitch" ref={ref}>
      <button
        type="button"
        className="teamSwitch-btn"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Users size={14} />
        <div className="teamSwitch-text">
          <strong>{label}</strong>
          {sublabel && <span>{sublabel}</span>}
        </div>
        <ChevronDown size={14} className={open ? "teamSwitch-chev teamSwitch-chev--open" : "teamSwitch-chev"} />
      </button>

      {open && (
        <div className="teamSwitch-menu" role="menu">
          <div className="teamSwitch-menu-head">Switch team</div>
          {elevated && (
            <button
              type="button"
              role="menuitem"
              className={isAllTeams ? "teamSwitch-item teamSwitch-item--active" : "teamSwitch-item"}
              onClick={() => pick("all")}
            >
              <div className="teamSwitch-item-text">
                <strong>All Teams</strong>
                <span>no filter · leadership view</span>
              </div>
              {isAllTeams && <Check size={14} />}
            </button>
          )}
          {availableTeams.length === 0 && (
            <div className="teamSwitch-empty">You aren't on any team yet.</div>
          )}
          {availableTeams.map(t => {
            const selected = !isAllTeams && activeTeam?.id === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="menuitem"
                className={selected ? "teamSwitch-item teamSwitch-item--active" : "teamSwitch-item"}
                onClick={() => pick(t.id)}
              >
                <div className="teamSwitch-item-text">
                  <strong>{t.name}</strong>
                  <span>{t.sport}</span>
                </div>
                {selected && <Check size={14} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
